import React, { useEffect, useState } from "react";
import { Resource } from "react-admin";
import { useTokenOps } from "../components/TokenContext";
import {
  CarbonAdminPage,
  CarbonFeedback,
} from "./corporateCarbon/CarbonAdminPage";
import { carbonRequest } from "./corporateCarbon/api";
import {
  FactorTable,
  combustionColumns,
  electricityColumns,
  refrigerantColumns,
} from "./CarbonFactorReleaseResource";

const factorCategories = {
  combustion: {
    label: "Combustion factors",
    menuLabel: "Combustion",
    columns: combustionColumns,
  },
  refrigerant: {
    label: "Refrigerant factors",
    menuLabel: "Refrigerants",
    columns: refrigerantColumns,
  },
  electricity: {
    label: "Electricity factors",
    menuLabel: "Electricity",
    columns: electricityColumns,
  },
};

const releaseTimestamp = (release) =>
  new Date(release.publishedAt || release.createdAt || 0).getTime();

export const selectCurrentPublishedRelease = (releases = []) =>
  releases
    .filter((release) => release.status === "published")
    .sort(
      (left, right) => releaseTimestamp(right) - releaseTimestamp(left),
    )[0] || null;

const normalizeRows = (response) =>
  response?.data || response?.items || response || [];

export const CurrentCarbonFactorPage = ({ category }) => {
  const tokenOps = useTokenOps();
  const token = tokenOps.account?.accessToken;
  const definition = factorCategories[category];
  const [release, setRelease] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [hasPublishedRelease, setHasPublishedRelease] = useState(true);

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await carbonRequest(
          token,
          "corporate-carbon/factor-releases",
        );
        const current = selectCurrentPublishedRelease(normalizeRows(response));
        if (!active) return;
        if (!current) {
          setRelease(null);
          setHasPublishedRelease(false);
          return;
        }
        const details = await carbonRequest(
          token,
          `corporate-carbon/factor-releases/${current.id}`,
        );
        if (active) {
          setRelease(details);
          setHasPublishedRelease(true);
        }
      } catch (requestError) {
        if (active) setError(requestError.message);
      } finally {
        if (active) setLoading(false);
      }
    };

    if (token) load();
    else setLoading(false);

    return () => {
      active = false;
    };
  }, [token]);

  if (!definition) return null;

  const releaseLabel = release
    ? `${release.version}${release.name ? ` — ${release.name}` : ""}`
    : "";

  return (
    <CarbonAdminPage
      title={definition.label}
      description={
        release
          ? `Current published release: ${releaseLabel}`
          : "Factors currently approved for carbon calculations."
      }
    >
      <CarbonFeedback
        loading={loading}
        loadingMessage="Loading current emission factors…"
      />
      <CarbonFeedback error={error} />
      <CarbonFeedback
        empty={!loading && !error && !hasPublishedRelease}
        emptyMessage="No published emission factor release is available."
      />
      {release ? (
        <FactorTable
          columns={definition.columns}
          label={definition.label}
          rows={release[category] || []}
        />
      ) : null}
    </CarbonAdminPage>
  );
};

const currentFactorResource = (name, label, category) => (
  <Resource
    name={name}
    options={{ label, menuGroup: "Bcome Carbon" }}
    list={() => <CurrentCarbonFactorPage category={category} />}
  />
);

export const carbonCombustionFactorResource = () =>
  currentFactorResource(
    "carbonCombustionFactors",
    "Combustion factors",
    "combustion",
  );

export const carbonRefrigerantFactorResource = () =>
  currentFactorResource(
    "carbonRefrigerantFactors",
    "Refrigerant factors",
    "refrigerant",
  );

export const carbonElectricityFactorResource = () =>
  currentFactorResource(
    "carbonElectricityFactors",
    "Electricity factors",
    "electricity",
  );
